import { keyBy } from 'lodash';
import React, { useCallback, useMemo } from 'react';

import { MenuItem, SelectChangeEvent } from '@mui/material';
import { Column } from '@tanstack/react-table';

import { emptyArray } from 'main/components/SearchBar/constants';

import Checkbox from './components/Checkbox';
import useStyles from './styles';

import Select from '../../../Select';

interface Props<T> {
    label: React.ReactNode;
    column: Column<T>;
    className?: string;
}

function FacetFilter<T>({ label, column, className }: Props<T>) {
    const { classes } = useStyles();
    const value = (column.getFilterValue() ?? emptyArray) as unknown[];
    const options = useMemo(
        () => [...column.getFacetedUniqueValues().entries()].sort(([keyA], [keyB]) => keyA.localeCompare(keyB)),
        [column]
    );
    const total = useMemo(() => options.reduce((output, [_, count]) => output + count, 0), [options]);

    const onChange = useCallback(
        (event: SelectChangeEvent<unknown[]>) => column.setFilterValue(event.target.value || []),
        [column]
    );

    const toggleAll = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            if (value.length === 0 || value.length < options.length) {
                column.setFilterValue(options.map(([option]) => option));
            }
            if (value.length === options.length) {
                column.setFilterValue([]);
            }
        },
        [column, options, value.length]
    );

    const renderValue = useCallback(
        (value: unknown[]) => {
            if (value.length === options.length) {
                const count = options.reduce((output, [_, qty]) => output + qty, 0);
                const label = `All ${count}`;
                return (
                    <span className={classes.chip} title={label}>
                        {label}
                    </span>
                );
            }
            if (value.length === 0) {
                return '---';
            }
            if (value.length === 1) {
                const count = options.find(([key]) => key === value[0])?.[1] ?? 0;
                const label = `${value[0]} ${count}`;
                return (
                    <span className={classes.chip} title={label}>
                        {label}
                    </span>
                );
            }

            const total = options.reduce((output, [key, qty]) => (value.includes(key) ? output + qty : output), 0);
            const dictionary = keyBy(options, ([key]) => key);
            const label = `${value.length} (${total})`;
            const title = value.map((name) => `${name}: ${dictionary[String(name)]?.[1] ?? 0}`).join('\n');
            return (
                <span className={classes.chip} title={title}>
                    {label}
                </span>
            );
        },
        [classes.chip, options]
    );

    return (
        <Select<unknown[]>
            fullWidth
            multiple
            value={value}
            startAdornment={label}
            size="small"
            classes={{ select: classes.select }}
            onChange={onChange}
            className={className}
            displayEmpty
            MenuProps={{ variant: 'menu', autoFocus: false }}
            renderValue={renderValue}
        >
            <MenuItem onClickCapture={toggleAll}>
                <span className={classes.key}>{'All'}</span>
                <div className={classes.container}>
                    <span className={classes.count}>{total}</span>
                    <Checkbox
                        checked={value.length === options.length}
                        indeterminate={value.length > 0 && value.length !== options.length}
                        color="secondary"
                        size="small"
                    />
                </div>
            </MenuItem>
            {options.map(([key, count]) => (
                <MenuItem key={key} value={key}>
                    <span className={classes.key}>{key}</span>
                    <div className={classes.container}>
                        <span className={classes.count}>{count}</span>
                        <Checkbox checked={value.includes(key)} color="secondary" size="small" />
                    </div>
                </MenuItem>
            ))}
        </Select>
    );
}

export default FacetFilter;
